import { BOOKING_STEP_MINUTES, PIXELS_PER_MINUTE } from '@pages/Calendar/data';

export const toMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);

  return h * 60 + m;
};

export const formatTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;

  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const snapToStep = (value: number, step: number): number =>
  Math.round(value / step) * step;

const MIN_DURATION_MINUTES = BOOKING_STEP_MINUTES;

export interface DragRange {
  newStart: string;
  newEnd: string;
  newTopPx: number;
  newHeightPx: number;
}

export const calculateDraggedRange = (params: {
  deltaPixels: number;
  originalStartMinutes: number;
  durationMinutes: number;
  calendarStartMinutes: number;
  calendarEndMinutes: number;
  stepMinutes?: number;
  pixelsPerMinute?: number;
}): DragRange => {
  const {
    deltaPixels,
    originalStartMinutes,
    durationMinutes,
    calendarStartMinutes,
    calendarEndMinutes,
    stepMinutes = BOOKING_STEP_MINUTES,
    pixelsPerMinute = PIXELS_PER_MINUTE,
  } = params;

  const deltaMinutes = deltaPixels / pixelsPerMinute;
  const rawNewStart = originalStartMinutes + deltaMinutes;
  const snappedNewStart = snapToStep(rawNewStart, stepMinutes);

  const minStart = calendarStartMinutes;
  const maxStart = calendarEndMinutes - durationMinutes;
  const clampedStart = Math.max(minStart, Math.min(maxStart, snappedNewStart));

  const newEnd = clampedStart + durationMinutes;

  return {
    newStart: formatTime(clampedStart),
    newEnd: formatTime(newEnd),
    newTopPx: (clampedStart - calendarStartMinutes) * pixelsPerMinute,
    newHeightPx: durationMinutes * pixelsPerMinute,
  };
};

export const calculateResizedBottomRange = (params: {
  deltaPixels: number;
  originalStartMinutes: number;
  originalEndMinutes: number;
  calendarStartMinutes: number;
  calendarEndMinutes: number;
  stepMinutes?: number;
  pixelsPerMinute?: number;
}): DragRange => {
  const {
    deltaPixels,
    originalStartMinutes,
    originalEndMinutes,
    calendarStartMinutes,
    calendarEndMinutes,
    stepMinutes = BOOKING_STEP_MINUTES,
    pixelsPerMinute = PIXELS_PER_MINUTE,
  } = params;

  const deltaMinutes = deltaPixels / pixelsPerMinute;
  const rawNewEnd = originalEndMinutes + deltaMinutes;
  const snappedNewEnd = snapToStep(rawNewEnd, stepMinutes);

  const minEnd = originalStartMinutes + MIN_DURATION_MINUTES;
  const maxEnd = calendarEndMinutes;
  const clampedEnd = Math.max(minEnd, Math.min(maxEnd, snappedNewEnd));

  return {
    newStart: formatTime(originalStartMinutes),
    newEnd: formatTime(clampedEnd),
    newTopPx: (originalStartMinutes - calendarStartMinutes) * pixelsPerMinute,
    newHeightPx: (clampedEnd - originalStartMinutes) * pixelsPerMinute,
  };
};

/**
 * Walk the element stack at a viewport coordinate and return the first
 * data-employee-column-id value found (set on EmployeesViewport column wrappers).
 * Works even when a high-z-index dragged card is on top, because
 * document.elementsFromPoint returns all stacked elements regardless of z-index.
 */
export const getTargetEmployeeIdFromPoint = (
  clientX: number,
  clientY: number
): string | null => {
  const elements = document.elementsFromPoint(clientX, clientY);

  for (const el of elements) {
    if (el instanceof HTMLElement && el.dataset.employeeColumnId) {
      return el.dataset.employeeColumnId;
    }
  }

  return null;
};

export const calculateResizedTopRange = (params: {
  deltaPixels: number;
  originalStartMinutes: number;
  originalEndMinutes: number;
  calendarStartMinutes: number;
  stepMinutes?: number;
  pixelsPerMinute?: number;
}): DragRange => {
  const {
    deltaPixels,
    originalStartMinutes,
    originalEndMinutes,
    calendarStartMinutes,
    stepMinutes = BOOKING_STEP_MINUTES,
    pixelsPerMinute = PIXELS_PER_MINUTE,
  } = params;

  const deltaMinutes = deltaPixels / pixelsPerMinute;
  const rawNewStart = originalStartMinutes + deltaMinutes;
  const snappedNewStart = snapToStep(rawNewStart, stepMinutes);

  const maxStart = originalEndMinutes - MIN_DURATION_MINUTES;
  const clampedStart = Math.max(
    calendarStartMinutes,
    Math.min(maxStart, snappedNewStart)
  );

  return {
    newStart: formatTime(clampedStart),
    newEnd: formatTime(originalEndMinutes),
    newTopPx: (clampedStart - calendarStartMinutes) * pixelsPerMinute,
    newHeightPx: (originalEndMinutes - clampedStart) * pixelsPerMinute,
  };
};
